// A plan's 3PL bookings: each 3PL truck (its whole truckload, one or more
// trips) with its load, the model's estimate, and the carrier it is requested
// from or booked with. The dispatcher picks a carrier from the shared directory
// (or adds one), books it, cancels with why, records the negotiated charges
// (append-only: a wrong line is voided, with why) and, when the plan changed a
// booked truck, confirms the carrier was told. Where a carrier shows, so do its
// contact details, which can be edited there, or the carrier deactivated; a new
// name like one already in the list (deactivated ones too) is flagged before
// it is added. The server owns every rule;
// each change names the versions it was made on and answers with the plan's
// bookings as they now are. Bookings wait while the plan has unsaved edits.

import { type CSSProperties, Fragment, useEffect, useState } from "react";

import {
  ApiError,
  addCarrier,
  addCharge,
  assignCarrier,
  bookAssignment,
  cancelAssignment,
  confirmCarrierInformed,
  editCarrier,
  getPlanCarriers,
  listCarriers,
  voidCharge,
} from "../api/client";
import { formatUsd } from "../lib/format";
import { type MsgKey, useT } from "../lib/i18n";
import type { Carrier, CarrierAssignment, ChargeKind, PlanCarriers, ThreePLTruck } from "../types";

const KINDS: ChargeKind[] = ["linehaul", "accessorial", "adjustment"];
const FIELDS = ["name", "contact_name", "phone", "email", "notes", "ushipment_supplier_id"] as const;
type Field = (typeof FIELDS)[number];
// Words that don't tell two companies apart.
const SUFFIXES = ["inc", "llc", "ltd", "co", "corp", "corporation", "company", "the"];

export function CarrierBookings({
  planId,
  planVersion,
  dirty,
  onTrucksBooked,
}: {
  planId: string;
  planVersion: number;
  dirty: boolean;
  // The trucks with a booking on record, cancelled ones too.
  onTrucksBooked: (truckIds: string[]) => void;
}) {
  const t = useT();
  const [data, setData] = useState<PlanCarriers | null>(null);
  const [directory, setDirectory] = useState<Carrier[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function show(next: PlanCarriers) {
    setData(next);
    onTrucksBooked([...new Set(next.assignments.map((a) => a.truck_id))].sort());
  }

  async function load() {
    try {
      show(await getPlanCarriers(planId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, planVersion]);

  useEffect(() => {
    listCarriers(true)
      .then(setDirectory)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  // Make a change; on a refusal, say why and read the bookings again.
  async function change(action: () => Promise<PlanCarriers>) {
    setBusy(true);
    setError(null);
    try {
      show(await action());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      if (e instanceof ApiError) void load();
    } finally {
      setBusy(false);
    }
  }

  // A new carrier for the directory; when the name is taken, the one there.
  async function newCarrier(name: string): Promise<string | null> {
    setError(null);
    try {
      const added = await addCarrier({ name });
      setDirectory((d) => [...d, added].sort((a, b) => a.name.localeCompare(b.name)));
      return added.id;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      const taken = e instanceof ApiError ? (e.detail as { carrier_id?: string }).carrier_id : null;
      return taken ?? null;
    }
  }

  // Change a carrier in the directory; the bookings read its new name again.
  async function saveCarrier(id: string, changes: Partial<Carrier>): Promise<boolean> {
    setError(null);
    try {
      const saved = await editCarrier(id, changes);
      setDirectory((d) => d.map((c) => (c.id === id ? saved : c)));
      if ("name" in changes) void load();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    }
  }

  if (!data) return error ? <p className="upload-status error">{error}</p> : null;
  if (data.trucks.length === 0) return null;

  const count = (state: ThreePLTruck["state"]) => data.trucks.filter((tr) => tr.state === state).length;
  const unassigned = count("unassigned");
  const requested = count("requested");
  const live = data.assignments.filter((a) => a.status !== "cancelled");
  const estimate = data.trucks.reduce((n, tr) => n + (tr.estimate_usd ?? 0), 0);
  const negotiated = live.reduce((n, a) => n + a.negotiated_usd, 0);
  const locked = busy || dirty;
  return (
    <section className="section-block" aria-label={t("bookings.title")}>
      <div className="section-header">
        <h3>{t("bookings.title")}</h3>
      </div>
      <p className="plan-muted">
        {t("bookings.summary", { booked: count("booked"), n: data.trucks.length })}
        {requested > 0 && ` · ${t("bookings.requested", { n: requested })}`}
        {unassigned > 0 && ` · ${t("bookings.unassigned", { n: unassigned })}`}
        {" · "}
        {t("bookings.totals", { negotiated: formatUsd(negotiated), estimate: formatUsd(estimate) })}
      </p>
      {dirty && <p className="plan-stale-note">{t("bookings.saveFirst")}</p>}
      {error && <p className="upload-status error">{error}</p>}
      <table className="manifest">
        <thead>
          <tr>
            <th>{t("bookings.col.truck")}</th>
            <th>{t("bookings.col.load")}</th>
            <th>{t("bookings.col.carrier")}</th>
            <th>{t("bookings.col.state")}</th>
            <th>{t("bookings.col.estimate")}</th>
            <th>{t("bookings.col.negotiated")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.trucks.map((truck) => (
            <BookingRow
              key={truck.truck_id}
              truck={truck}
              active={data.assignments.find((a) => a.id === truck.assignment_id) ?? null}
              history={data.assignments.filter(
                (a) => a.truck_id === truck.truck_id && a.status === "cancelled",
              )}
              planId={planId}
              planVersion={data.plan_version}
              directory={directory}
              locked={locked}
              change={change}
              newCarrier={newCarrier}
              saveCarrier={saveCarrier}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function BookingRow({
  truck,
  active,
  history,
  planId,
  planVersion,
  directory,
  locked,
  change,
  newCarrier,
  saveCarrier,
}: {
  truck: ThreePLTruck;
  active: CarrierAssignment | null;
  history: CarrierAssignment[];
  planId: string;
  planVersion: number;
  directory: Carrier[];
  locked: boolean;
  change: (action: () => Promise<PlanCarriers>) => Promise<void>;
  newCarrier: (name: string) => Promise<string | null>;
  saveCarrier: (id: string, changes: Partial<Carrier>) => Promise<boolean>;
}) {
  const t = useT();
  const [carrierId, setCarrierId] = useState("");
  const [adding, setAdding] = useState<string | null>(null); // the new carrier's name
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const id = truck.truck_id;
  const carrier = directory.find((c) => c.id === (active?.carrier_id ?? carrierId)) ?? null;
  const similar = adding?.trim() ? directory.filter((c) => looksLike(c.name, adding)) : [];

  // A carrier already in the list instead of a new one; a deactivated one comes back.
  async function useExisting(c: Carrier) {
    if (!c.active && !(await saveCarrier(c.id, { active: true }))) return;
    setCarrierId(c.id);
    setAdding(null);
  }

  async function saveShown(changes: Partial<Carrier>): Promise<boolean> {
    const saved = await saveCarrier(carrier!.id, changes);
    if (saved && changes.active === false && carrierId === carrier!.id) setCarrierId("");
    return saved;
  }

  function assign(status: "requested" | "booked") {
    void change(() =>
      assignCarrier(planId, { truck_id: id, carrier_id: carrierId, status, plan_version: planVersion }),
    );
  }

  function cancel(a: CarrierAssignment) {
    const reason = window.prompt(t("bookings.cancelReason"))?.trim();
    if (reason) void change(() => cancelAssignment(a.id, a.version, reason));
  }

  async function add() {
    const picked = await newCarrier(adding!.trim());
    if (picked) {
      setCarrierId(picked);
      setAdding(null);
    }
  }

  return (
    <Fragment>
      <tr aria-label={t("bookings.truck", { truck: id })}>
        <td>{id}</td>
        <td>
          {[
            t("bookings.trips", { n: truck.route_ids.length }),
            t("plans.orders", { n: truck.order_nos.length }),
            t("bookings.pallets", { n: truck.pallets }),
          ].join(" · ")}
          {truck.booked_elsewhere.length > 0 && (
            <div className="upload-status error">
              {t("bookings.bookedElsewhere", { orders: truck.booked_elsewhere.join(", ") })}
            </div>
          )}
        </td>
        <td>
          {active ? (
            <div>{active.carrier_name}</div>
          ) : (
            <div style={inline}>
              <label className="control">
                <span className="control-label">{t("bookings.col.carrier")}</span>
                <select value={carrierId} onChange={(e) => setCarrierId(e.target.value)}>
                  <option value="">— {t("bookings.choose")} —</option>
                  {directory
                    .filter((c) => c.active)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </label>
              {adding === null ? (
                <button className="btn btn-ghost" onClick={() => setAdding("")}>
                  {t("bookings.newCarrier")}
                </button>
              ) : (
                <>
                  <label className="control">
                    <span className="control-label">{t("bookings.carrierName")}</span>
                    <input value={adding} onChange={(e) => setAdding(e.target.value)} />
                  </label>
                  <button className="btn" disabled={!adding.trim()} onClick={() => void add()}>
                    {t("bookings.add")}
                  </button>
                  {similar.map((c) => (
                    <div key={c.id} className="plan-muted">
                      {t(c.active ? "bookings.similar" : "bookings.similarInactive", {
                        name: c.name,
                      })}{" "}
                      <button className="btn btn-ghost" onClick={() => void useExisting(c)}>
                        {t(c.active ? "bookings.useIt" : "bookings.reactivate", { name: c.name })}
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
          {carrier && (
            <div className="plan-muted">
              {[carrier.contact_name, carrier.phone, carrier.email].some(Boolean) && (
                <div>
                  {[carrier.contact_name, carrier.phone, carrier.email].filter(Boolean).join(" · ")}
                </div>
              )}
              {carrier.notes && <div>{carrier.notes}</div>}
              <button
                className="btn btn-ghost"
                aria-expanded={editing}
                onClick={() => setEditing(!editing)}
              >
                {t("bookings.editCarrier")}
              </button>
            </div>
          )}
        </td>
        <td>
          <span style={{ ...pill, ...STATE_STYLE[truck.state] }}>
            {t(`bookings.state.${truck.state}` as MsgKey)}
          </span>
          {truck.changed_since_booked && (
            <div className="upload-status error">{t("bookings.changed")}</div>
          )}
        </td>
        <td>{truck.estimate_usd === null ? "—" : formatUsd(truck.estimate_usd)}</td>
        <td>
          {active && active.negotiated_usd ? formatUsd(active.negotiated_usd) : "—"}
          {active && active.cost_status !== "open" && (
            <div className="plan-muted">{t(`costs.status.${active.cost_status}` as MsgKey)}</div>
          )}
        </td>
        <td>
          <div style={inline}>
            {!active && (
              <>
                <button className="btn" disabled={locked || !carrierId} onClick={() => assign("booked")}>
                  {t("bookings.book")}
                </button>
                <button
                  className="btn btn-ghost"
                  disabled={locked || !carrierId}
                  onClick={() => assign("requested")}
                >
                  {t("bookings.request")}
                </button>
              </>
            )}
            {active?.status === "requested" && (
              <button
                className="btn"
                disabled={locked}
                onClick={() => void change(() => bookAssignment(active.id, active.version, planVersion))}
              >
                {t("bookings.markBooked")}
              </button>
            )}
            {active && truck.changed_since_booked && (
              <button
                className="btn"
                disabled={locked}
                onClick={() =>
                  void change(() => confirmCarrierInformed(active.id, active.version, planVersion))
                }
              >
                {t("bookings.informed")}
              </button>
            )}
            {active && (
              <button className="btn btn-ghost" disabled={locked} onClick={() => cancel(active)}>
                {t(active.status === "booked" ? "bookings.cancelBooking" : "bookings.cancelRequest")}
              </button>
            )}
            {(active || history.length > 0) && (
              <button className="btn btn-ghost" aria-expanded={open} onClick={() => setOpen(!open)}>
                {t("bookings.details")}
              </button>
            )}
          </div>
        </td>
      </tr>
      {editing && carrier && (
        <tr>
          <td colSpan={7}>
            <CarrierForm
              key={carrier.id}
              carrier={carrier}
              save={saveShown}
              onDone={() => setEditing(false)}
            />
          </td>
        </tr>
      )}
      {open && (
        <tr>
          <td colSpan={7}>
            <Charges truckId={id} active={active} history={history} locked={locked} change={change} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

// A carrier's directory entry: its name, how to reach it, notes and its
// uShipment id, or deactivating it so it can't be booked again.
function CarrierForm({
  carrier,
  save,
  onDone,
}: {
  carrier: Carrier;
  save: (changes: Partial<Carrier>) => Promise<boolean>;
  onDone: () => void;
}) {
  const t = useT();
  const [values, setValues] = useState(() =>
    Object.fromEntries(FIELDS.map((f) => [f, carrier[f] ?? ""])) as Record<Field, string>,
  );

  async function submit() {
    const changes = Object.fromEntries(FIELDS.map((f) => [f, values[f].trim() || null]));
    if (await save(changes)) onDone();
  }

  async function deactivate() {
    if (window.confirm(t("bookings.deactivateConfirm", { name: carrier.name }))) {
      if (await save({ active: false })) onDone();
    }
  }

  return (
    <section aria-label={t("bookings.carrierDetails", { name: carrier.name })} style={box}>
      <div style={inline}>
        {FIELDS.map((f) => (
          <label key={f} className="control">
            <span className="control-label">{t(`bookings.field.${f}` as MsgKey)}</span>
            <input
              value={values[f]}
              onChange={(e) => setValues({ ...values, [f]: e.target.value })}
            />
          </label>
        ))}
      </div>
      <div style={inline}>
        <button className="btn" disabled={!values.name.trim()} onClick={() => void submit()}>
          {t("bookings.save")}
        </button>
        {carrier.active && (
          <button className="btn btn-ghost" onClick={() => void deactivate()}>
            {t("bookings.deactivate")}
          </button>
        )}
      </div>
    </section>
  );
}

// Two names that may be the same company: equal once case, punctuation and
// words like "Inc." are dropped, or one inside the other.
function looksLike(a: string, b: string): boolean {
  const key = (name: string) =>
    name
      .toLowerCase()
      .split(/[^\p{L}\p{N}]+/u)
      .filter((w) => w && !SUFFIXES.includes(w))
      .join("");
  const [x, y] = [key(a), key(b)];
  if (!x || !y) return false;
  return x === y || (Math.min(x.length, y.length) >= 4 && (x.includes(y) || y.includes(x)));
}

// A booking's charge lines, adding and voiding them, and the truck's earlier
// (cancelled) bookings.
function Charges({
  truckId,
  active,
  history,
  locked,
  change,
}: {
  truckId: string;
  active: CarrierAssignment | null;
  history: CarrierAssignment[];
  locked: boolean;
  change: (action: () => Promise<PlanCarriers>) => Promise<void>;
}) {
  const t = useT();
  const [kind, setKind] = useState<ChargeKind>("linehaul");
  const [amount, setAmount] = useState("");
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");

  async function add(a: CarrierAssignment) {
    await change(() =>
      addCharge(a.id, {
        version: a.version,
        kind,
        amount_usd: Number(amount),
        ...(code.trim() && { code: code.trim() }),
        ...(note.trim() && { note: note.trim() }),
      }),
    );
    setAmount("");
    setCode("");
    setNote("");
  }

  function voidLine(a: CarrierAssignment, chargeId: string) {
    const reason = window.prompt(t("bookings.voidReason"))?.trim();
    if (reason) void change(() => voidCharge(a.id, chargeId, a.version, reason));
  }

  return (
    <section aria-label={t("bookings.chargesFor", { truck: truckId })} style={box}>
      {active && (
        <>
          <ul style={list}>
            {active.charges.map((c) => (
              <li key={c.id} style={c.voided_at ? { opacity: 0.6 } : undefined}>
                <span style={c.voided_at ? { textDecoration: "line-through" } : undefined}>
                  {`${t(`bookings.kind.${c.kind}` as MsgKey)} ${formatUsd(c.amount_usd)}` +
                    (c.code ? ` (${c.code})` : "") +
                    (c.note ? ` — ${c.note}` : "")}
                </span>
                {` · ${c.entered_by}`}
                {c.voided_at ? (
                  ` · ${t("bookings.voided", { reason: c.void_reason ?? "" })}`
                ) : (
                  <button
                    className="btn btn-ghost"
                    disabled={locked}
                    onClick={() => voidLine(active, c.id)}
                  >
                    {t("bookings.void")}
                  </button>
                )}
              </li>
            ))}
          </ul>
          <div style={inline}>
            <label className="control">
              <span className="control-label">{t("bookings.kind")}</span>
              <select value={kind} onChange={(e) => setKind(e.target.value as ChargeKind)}>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`bookings.kind.${k}` as MsgKey)}
                  </option>
                ))}
              </select>
            </label>
            <label className="control">
              <span className="control-label">{t("bookings.amount")}</span>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </label>
            <label className="control">
              <span className="control-label">{t("bookings.code")}</span>
              <input value={code} onChange={(e) => setCode(e.target.value)} />
            </label>
            <label className="control">
              <span className="control-label">{t("bookings.note")}</span>
              <input value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <button
              className="btn"
              disabled={locked || amount === "" || Number.isNaN(Number(amount))}
              onClick={() => void add(active)}
            >
              {t("bookings.addCharge")}
            </button>
          </div>
        </>
      )}
      {history.length > 0 && (
        <ul style={list}>
          {history.map((a) => (
            <li key={a.id} className="plan-muted">
              {t("bookings.wasCancelled", {
                carrier: a.carrier_name ?? a.carrier_id,
                reason: a.cancel_reason ?? "",
              })}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const STATE_STYLE: Record<ThreePLTruck["state"], CSSProperties> = {
  unassigned: { background: "rgba(232,72,85,0.12)", color: "#a4161a" },
  requested: { background: "rgba(234,88,12,0.14)", color: "#c2410c" },
  booked: { background: "rgba(22,163,74,0.14)", color: "#15803d" },
};
const pill: CSSProperties = {
  display: "inline-block",
  padding: "1px 8px",
  borderRadius: 999,
  fontSize: 12,
  whiteSpace: "nowrap",
};
const inline: CSSProperties = { display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" };
const box: CSSProperties = { display: "grid", gap: 8, padding: "8px 4px" };
const list: CSSProperties = { margin: 0, paddingLeft: 18, lineHeight: 1.8 };
